import { Loader2, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

export type ExportScope = 'all' | 'selected' | 'filtered';

interface GridExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (scope: ExportScope) => void;
  selectedCount: number;
  hasActiveFilter: boolean;
  totalCount: number;
  isExporting?: boolean;
  title?: string;
}

export default function GridExportDialog({
  open,
  onOpenChange,
  onExport,
  selectedCount,
  hasActiveFilter,
  totalCount,
  isExporting = false,
  title = 'Export Items',
}: GridExportDialogProps) {
  const [scope, setScope] = useState<ExportScope>('all');

  // Reset scope when dialog opens
  useEffect(() => {
    if (open) {
      setScope('all');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Choose which records to include in the CSV export.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={scope}
            onValueChange={(v) => setScope(v as ExportScope)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all" id="export-all" />
              <Label htmlFor="export-all" className="font-normal cursor-pointer">
                All Items ({totalCount.toLocaleString()})
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="selected"
                id="export-selected"
                disabled={selectedCount === 0}
              />
              <Label
                htmlFor="export-selected"
                className={`font-normal cursor-pointer ${selectedCount === 0 ? 'text-muted-foreground' : ''}`}
              >
                Only Selected Items ({selectedCount})
                {selectedCount === 0 && (
                  <span className="text-xs ml-1">(none selected)</span>
                )}
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="filtered"
                id="export-filtered"
                disabled={!hasActiveFilter}
              />
              <Label
                htmlFor="export-filtered"
                className={`font-normal cursor-pointer ${!hasActiveFilter ? 'text-muted-foreground' : ''}`}
              >
                Only Filtered Items
                {!hasActiveFilter && (
                  <span className="text-xs ml-1">(no filter active)</span>
                )}
              </Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={() => onExport(scope)} disabled={isExporting}>
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
